import { http } from './http';
import { ROUTES } from './config';
import type {
  AdminNotificationPage,
  AppNotificationPage,
  EmailTemplate,
  StaffNotificationPage,
  TestSendResult,
  UpsertTemplateInput,
} from '../types/domain';

/**
 * NotificationsController client. The owning customer, or manager/admin — the backend 403s a
 * mismatch (see NotificationsController.ListForUser/MarkRead's IsStaff()-gated ownership check,
 * same pattern as ordersApi).
 */
export const notificationsApi = {
  listForUser(customerId: string, cursor?: string, pageSize = 20): Promise<AppNotificationPage> {
    const params = new URLSearchParams({ pageSize: String(pageSize) });
    if (cursor) params.set('cursor', cursor);
    return http.get(`${ROUTES.notifications}/user/${customerId}?${params.toString()}`);
  },

  /** Manager+ only — the store team's own notifications + unread count (admin bell). */
  listStaff(limit = 30): Promise<StaffNotificationPage> {
    return http.get(`${ROUTES.notifications}/staff?limit=${limit}`);
  },

  readAllStaff(): Promise<void> {
    return http.put(`${ROUTES.notifications}/staff/read-all`, undefined);
  },

  markRead(id: string): Promise<void> {
    return http.put(`${ROUTES.notifications}/${id}/read`, undefined);
  },

  /** Manager+ only — every customer's send log, not just one. */
  listAll(filters: { status?: string; channel?: string; cursor?: string; pageSize?: number } = {}): Promise<AdminNotificationPage> {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.channel) params.set('channel', filters.channel);
    if (filters.cursor) params.set('cursor', filters.cursor);
    if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
    const qs = params.toString();
    return http.get(`${ROUTES.notifications}${qs ? `?${qs}` : ''}`);
  },

  /** Manager+ only — send a real template to an arbitrary inbox to preview it end-to-end. */
  testSend(body: { templateKey: string; toEmail: string; sampleData?: Record<string, string | null> }): Promise<TestSendResult> {
    return http.post(`${ROUTES.notifications}/test-send`, body);
  },
};

/** NotificationTemplatesController client — manager+ only. */
export const notificationTemplatesApi = {
  list(): Promise<EmailTemplate[]> {
    return http.get(ROUTES.notificationTemplates);
  },

  get(key: string): Promise<EmailTemplate> {
    return http.get(`${ROUTES.notificationTemplates}/${key}`);
  },

  /** Creates the template if `key` doesn't exist yet, otherwise updates it in place. */
  upsert(key: string, body: UpsertTemplateInput): Promise<EmailTemplate> {
    return http.put(`${ROUTES.notificationTemplates}/${key}`, body);
  },
};
